import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FiUpload,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiX,
  FiImage,
  FiDownload,
  FiFilter,
  FiSearch,
  FiClock,
  FiLayers,
  FiPlus,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { FaCalendarAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../utils/config";
import "../index.css";
import "./EmployeeDashboard.css";

const MAX_IMAGES = 10;

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // View modal
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Upload / Edit modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [previews, setPreviews] = useState([]); // [{ file, url }]
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef(null);

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  const formatImageUrl = (filePath) => {
    if (!filePath) return "";
    let path = String(filePath).replace(/\\/g, "/");
    if (path.includes("uploads")) {
      path = "uploads/" + path.split(/uploads[\\/]/).pop();
    }
    return `${API_BASE_URL.replace("/api", "")}/${path.replace(/^\/+/, "")}`;
  };

  // images can be array of objects ({ imageUrl, _id }) or plain strings
  const getImages = (banner) =>
    (banner?.images || []).map((img, i) =>
      typeof img === "string"
        ? { _id: `img-${i}`, imageUrl: img }
        : { _id: img._id || `img-${i}`, imageUrl: img.imageUrl || img.url || "" }
    );

  const formatDate = (date, withTime = false) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
  };

  const getPeriodLabel = () =>
    new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  /* ------------------------------------------------------------------ */
  /* API                                                                 */
  /* ------------------------------------------------------------------ */

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_BASE_URL}/admin/allbanners`);
      const data = res.data;

      if (Array.isArray(data)) {
        setBanners(data);
      } else if (data?.success) {
        const list = data.banners || data.banner || data.data || [];
        setBanners(Array.isArray(list) ? list : [list]);
      } else {
        setBanners([]);
        setError(data?.message || "Failed to fetch banners");
      }
    } catch (err) {
      console.error("Fetch banners error:", err);
      setError("Failed to fetch banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleUpload = async () => {
    if (previews.length === 0) {
      toast.error("Please select at least one banner image.");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      previews.forEach((p) => formData.append("images", p.file));

      // While editing we hit the SAME upload API and pass the existing id,
      // so the backend can replace the old banner set (ignored if unused).
      if (editingBanner?._id) formData.append("bannerId", editingBanner._id);

      const res = await axios.post(`${API_BASE_URL}/admin/uploadbanner`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Banners uploaded successfully");
        closeUploadModal();
        fetchBanners();
      } else {
        toast.error(res.data?.message || "Failed to upload banners.");
      }
    } catch (err) {
      console.error("Upload banner error:", err);
      toast.error(err?.response?.data?.message || "Failed to upload banners.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      const res = await axios.delete(
        `${API_BASE_URL}/admin/deletebanner/${deleteTarget._id}`
      );

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Failed to delete banner.");
        return;
      }

      toast.success(res.data?.message || "Banner deleted successfully");
      setBanners((prev) => prev.filter((b) => b._id !== deleteTarget._id));

      if (selectedBanner?._id === deleteTarget._id) {
        setIsViewModalOpen(false);
        setSelectedBanner(null);
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error("Delete banner error:", err);
      toast.error(err?.response?.data?.message || "Failed to delete banner.");
    } finally {
      setDeleting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Modal handlers                                                      */
  /* ------------------------------------------------------------------ */

  const openUploadModal = () => {
    setEditingBanner(null);
    setPreviews([]);
    setIsUploadModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setPreviews([]);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    previews.forEach((p) => URL.revokeObjectURL(p.url));
    setPreviews([]);
    setEditingBanner(null);
    setIsDragOver(false);
    setIsUploadModalOpen(false);
  };

  const openViewModal = (banner) => {
    setSelectedBanner(banner);
    setPreviewImage(null);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setPreviewImage(null);
    setSelectedBanner(null);
    setIsViewModalOpen(false);
  };

  /* ------------------------------------------------------------------ */
  /* File picking                                                        */
  /* ------------------------------------------------------------------ */

  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );

    if (incoming.length === 0) {
      toast.error("Only image files are allowed.");
      return;
    }

    if (previews.length + incoming.length > MAX_IMAGES) {
      toast.error(`You can upload a maximum of ${MAX_IMAGES} images at a time.`);
      return;
    }

    setPreviews((prev) => [
      ...prev,
      ...incoming.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const removePreview = (index) => {
    setPreviews((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  /* ------------------------------------------------------------------ */
  /* Filter + pagination                                                 */
  /* ------------------------------------------------------------------ */

  const clearFilters = () => {
    setSearchQuery("");
    setDateFilter("");
    setCurrentPage(1);
  };

  const filteredBanners = banners.filter((banner) => {
    const q = searchQuery.trim().toLowerCase();
    const images = getImages(banner);

    const matchesSearch =
      !q ||
      (banner._id || "").toLowerCase().includes(q) ||
      images.some((img) => (img.imageUrl || "").toLowerCase().includes(q));

    let matchesDate = true;
    if (dateFilter && banner.createdAt) {
      matchesDate =
        new Date(banner.createdAt).toISOString().split("T")[0] === dateFilter;
    }

    return matchesSearch && matchesDate;
  });

  const totalPages = Math.ceil(filteredBanners.length / itemsPerPage) || 1;
  const paginatedBanners = filteredBanners.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisiblePages = () => {
    const pages = [];
    const total = totalPages;
    const current = currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push("...");
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < total) pages.push(i);
      }
      if (current < total - 2) pages.push("...");
      if (total > 1) pages.push(total);
    }
    return pages;
  };

  /* ------------------------------------------------------------------ */
  /* KPI values                                                          */
  /* ------------------------------------------------------------------ */

  const totalImages = banners.reduce(
    (sum, b) => sum + getImages(b).length,
    0
  );

  const latestUpload = banners
    .map((b) => b.createdAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  const avgPerSet =
    banners.length > 0 ? Math.round(totalImages / banners.length) : 0;

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />

      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Brand <span>Banners</span>
            </h1>
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">
              Upload, preview and manage promotional banner images.
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FaCalendarAlt />
              <span>{getPeriodLabel()}</span>
            </div>
            <button
              type="button"
              onClick={openUploadModal}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm"
            >
              <FiUpload size={14} /> Upload Banner
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Banner Sets</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiImage />
              </div>
            </div>
            <div className="emp-dash__stat-value">{banners.length}</div>
            <div className="emp-dash__stat-meta">banner groups uploaded</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Images</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiLayers />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-700">
              {totalImages}
            </div>
            <div className="emp-dash__stat-meta">images in the library</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Avg / Set</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiCheckCircle />
              </div>
            </div>
            <div className="emp-dash__stat-value text-violet-700">
              {avgPerSet}
            </div>
            <div className="emp-dash__stat-meta">images per banner group</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Filtered Results</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiFilter />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700">
              {filteredBanners.length}
            </div>
            <div className="emp-dash__stat-meta">matching active filter</div>
          </div>

          <div className="emp-dash__stat col-span-2 lg:col-span-1">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Latest Upload</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiClock />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700 text-base">
              {latestUpload ? formatDate(latestUpload) : "—"}
            </div>
            <div className="emp-dash__stat-meta">most recent banner added</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="emp-dash__card p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {/* Search */}
              <div className="relative min-w-[200px] flex-1 max-w-[320px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search banner id or image url..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>

              {/* Date filter */}
              <div className="relative">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-[34px] px-2.5 py-1 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium cursor-pointer"
                />
              </div>
            </div>

            {(searchQuery || dateFilter) && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all whitespace-nowrap cursor-pointer self-end lg:self-center"
              >
                <FiX size={13} /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Banners Table Card */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiImage size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Banner Library
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {paginatedBanners.length} of {filteredBanners.length}{" "}
                  banner sets
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold outline-none focus:border-indigo-600"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4 min-w-[260px]">Banner Preview</th>
                  <th className="py-3.5 px-4 text-center min-w-[100px]">
                    Images
                  </th>
                  <th className="py-3.5 px-4 min-w-[150px]">Uploaded On</th>
                  <th className="py-3.5 px-4 text-center min-w-[190px]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
                        <span className="text-xs text-gray-500 font-medium">
                          Loading banners...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-400 flex items-center justify-center mb-2">
                          <FiAlertCircle size={22} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          {error}
                        </p>
                        <button
                          onClick={fetchBanners}
                          className="mt-3 px-3 py-1.5 text-[11px] font-bold uppercase rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors cursor-pointer"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : filteredBanners.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiImage size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Banners Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          Upload your first banner or clear the active filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedBanners.map((banner, index) => {
                    const images = getImages(banner);
                    const visible = images.slice(0, 4);
                    const remaining = images.length - visible.length;

                    return (
                      <tr
                        key={banner._id}
                        className="hover:bg-indigo-50/20 transition-colors group"
                      >
                        {/* S.No */}
                        <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>

                        {/* Thumbnails */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2">
                            {visible.map((img, i) => (
                              <button
                                key={img._id || i}
                                type="button"
                                onClick={() => {
                                  setSelectedBanner(banner);
                                  setPreviewImage(formatImageUrl(img.imageUrl));
                                  setIsViewModalOpen(true);
                                }}
                                className="w-14 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0 hover:ring-2 hover:ring-indigo-500/40 transition-all cursor-pointer"
                                title="Preview image"
                              >
                                <img
                                  src={formatImageUrl(img.imageUrl)}
                                  alt={`banner-${i + 1}`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              </button>
                            ))}

                            {remaining > 0 && (
                              <span className="w-14 h-10 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center justify-center text-[11px] font-extrabold flex-shrink-0">
                                +{remaining}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Count */}
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <FiLayers size={10} /> {images.length}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="py-3.5 px-4 text-gray-600 text-[11px]">
                          <div className="font-medium">
                            {formatDate(banner.createdAt)}
                          </div>
                          <span className="text-[10px] text-gray-400">
                            {banner.updatedAt &&
                            banner.updatedAt !== banner.createdAt
                              ? `Updated ${formatDate(banner.updatedAt)}`
                              : "No updates yet"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openViewModal(banner)}
                              className="px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer"
                              title="View Banner"
                            >
                              <FiEye size={12} /> View
                            </button>

                            <button
                              type="button"
                              onClick={() => openEditModal(banner)}
                              className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-xs flex items-center gap-1 cursor-pointer"
                              title="Replace Banner Images"
                            >
                              <FiEdit2 size={12} /> Edit
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeleteTarget(banner)}
                              className="p-1.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Banner"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 flex-wrap gap-2">
              <span className="text-xs text-gray-500">
                Page <strong className="text-gray-900">{currentPage}</strong> of{" "}
                <strong className="text-gray-900">{totalPages}</strong>
              </span>

              <div className="flex items-center gap-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-colors"
                >
                  Previous
                </button>

                {getVisiblePages().map((page, i) => {
                  if (page === "...") {
                    return (
                      <span
                        key={`ellipsis-${i}`}
                        className="px-1 text-gray-400 text-xs font-bold"
                      >
                        …
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                        currentPage === page
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ============================================================= */}
      {/* VIEW BANNER MODAL                                             */}
      {/* ============================================================= */}
      {isViewModalOpen && selectedBanner && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  <FiImage size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    Banner Set
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {getImages(selectedBanner).length} Images · Uploaded{" "}
                    {formatDate(selectedBanner.createdAt, true)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeViewModal}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {getImages(selectedBanner).length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mx-auto mb-2">
                    <FiImage size={24} />
                  </div>
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    No images in this banner set
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {getImages(selectedBanner).map((img, i) => {
                    const url = formatImageUrl(img.imageUrl);
                    return (
                      <div
                        key={img._id || i}
                        className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
                      >
                        <img
                          src={url}
                          alt={`banner-${i + 1}`}
                          className="w-full h-36 object-cover"
                          loading="lazy"
                        />

                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-slate-900/70 text-white text-[10px] font-bold">
                          #{i + 1}
                        </span>

                        <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPreviewImage(url)}
                            className="p-2 rounded-lg bg-white text-indigo-700 hover:bg-indigo-50 transition-colors cursor-pointer"
                            title="View full size"
                          >
                            <FiEye size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => window.open(url, "_blank")}
                            className="p-2 rounded-lg bg-white text-indigo-700 hover:bg-indigo-50 transition-colors cursor-pointer"
                            title="Download image"
                          >
                            <FiDownload size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <span className="text-[11px] font-mono text-gray-400">
                ID: {selectedBanner._id}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    closeViewModal();
                    openEditModal(selectedBanner);
                  }}
                  className="py-2 px-4 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <FiEdit2 size={13} /> Edit
                </button>
                <button
                  type="button"
                  onClick={closeViewModal}
                  className="py-2 px-5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md"
          onClick={() => setPreviewImage(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewImage(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <FiX size={20} />
          </button>
          <img
            src={previewImage}
            alt="banner preview"
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ============================================================= */}
      {/* UPLOAD / EDIT MODAL                                           */}
      {/* ============================================================= */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  {editingBanner ? <FiEdit2 size={18} /> : <FiUpload size={18} />}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {editingBanner ? "Edit Banner Images" : "Upload Banners"}
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {editingBanner
                      ? `Replacing set ${editingBanner._id?.slice(-10)}`
                      : `Up to ${MAX_IMAGES} images per upload`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeUploadModal}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Existing images when editing */}
              {editingBanner && getImages(editingBanner).length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Current Images
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {getImages(editingBanner).map((img, i) => (
                      <div
                        key={img._id || i}
                        className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
                      >
                        <img
                          src={formatImageUrl(img.imageUrl)}
                          alt={`current-${i + 1}`}
                          className="w-full h-16 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dropzone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${
                  isDragOver
                    ? "border-indigo-500 bg-indigo-50/60"
                    : "border-gray-300 bg-gray-50/60 hover:border-indigo-400 hover:bg-indigo-50/30"
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3">
                  <FiPlus size={22} />
                </div>
                <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                  Click to browse or drag &amp; drop images
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  PNG, JPG, JPEG, WEBP · Max {MAX_IMAGES} images
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleFileSelect}
                />
              </div>

              {/* Selected previews */}
              {previews.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Selected ({previews.length}/{MAX_IMAGES})
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        previews.forEach((p) => URL.revokeObjectURL(p.url));
                        setPreviews([]);
                      }}
                      className="text-[10px] font-bold uppercase text-rose-600 hover:text-rose-700 cursor-pointer"
                    >
                      Clear all
                    </button>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {previews.map((p, i) => (
                      <div
                        key={i}
                        className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
                      >
                        <img
                          src={p.url}
                          alt={`preview-${i + 1}`}
                          className="w-full h-20 object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePreview(i);
                          }}
                          className="absolute top-1 right-1 w-6 h-6 rounded-lg bg-slate-900/70 hover:bg-rose-600 text-white flex items-center justify-center transition-colors cursor-pointer"
                          title="Remove"
                        >
                          <FiX size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <span className="text-[11px] text-gray-400 italic">
                {editingBanner
                  ? "New images will be uploaded via /uploadbanner"
                  : "Images will be uploaded via /uploadbanner"}
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="py-2 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading || previews.length === 0}
                  className="py-2 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      {editingBanner ? <FiEdit2 size={13} /> : <FiUpload size={13} />}
                      {editingBanner ? "Update Banner" : "Upload Banner"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* DELETE CONFIRM MODAL                                          */}
      {/* ============================================================= */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
                <FiTrash2 size={22} />
              </div>
              <h3 className="text-base font-bold text-gray-900">
                Delete Banner Set?
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                This will permanently remove{" "}
                <strong>{getImages(deleteTarget).length}</strong> image(s) from
                this banner group. This action cannot be undone.
              </p>
              <p className="text-[10px] font-mono text-gray-400 mt-2">
                ID: {deleteTarget._id}
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="py-2 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="py-2 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 size={13} /> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;